import { useState } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, errorText } from "@/lib/api";
import { OrderSuccess } from "@/components/OrderSuccess";

const field = "w-full rounded-xl border border-[#F0DDD3] bg-white px-4 py-3 text-sm text-cacao outline-none transition-colors focus:border-[#E11D48]";
const today = () => new Date().toISOString().slice(0, 10);

const BoxItem = ({ it }) => {
  const { setQty, update } = useCart();
  const tid = it.key.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <li className="rounded-2xl border border-[#F0DDD3] bg-white p-4" data-testid={`box-item-${tid}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="f-mono text-[11px] uppercase tracking-[0.18em] text-rose">{it.category}</p>
          <p className="mt-0.5 font-medium leading-snug text-cacao">{it.name}</p>
        </div>
        <button onClick={() => setQty(it.key, 0)} data-testid={`box-item-remove-${tid}`} aria-label="Remove" className="text-cacao-2 hover:text-[#E11D48]">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex items-center rounded-full border border-[#F0DDD3]">
          <button onClick={() => setQty(it.key, it.qty - 1)} className="grid h-8 w-8 place-items-center" data-testid={`box-item-dec-${tid}`} aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
          <input
            type="number"
            min="1"
            value={it.qty}
            onChange={(e) => setQty(it.key, Math.max(1, parseInt(e.target.value || "1", 10)))}
            data-testid={`box-item-qty-${tid}`}
            className="w-10 bg-transparent text-center text-sm font-semibold outline-none [appearance:textfield]"
          />
          <button onClick={() => setQty(it.key, it.qty + 1)} className="grid h-8 w-8 place-items-center" data-testid={`box-item-inc-${tid}`} aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
        </div>
        <input
          value={it.note}
          onChange={(e) => update(it.key, { note: e.target.value })}
          placeholder="Note (colors, name, theme…)"
          data-testid={`box-item-note-${tid}`}
          className="min-w-0 flex-1 rounded-full border border-[#F0DDD3] px-3 py-1.5 text-xs outline-none focus:border-[#E11D48]"
        />
      </div>
    </li>
  );
};

const CheckoutForm = ({ onDone }) => {
  const { items, clear } = useCart();
  const [f, setF] = useState({ customer_name: "", phone: "", email: "", fulfillment: "pickup", date_needed: "", occasion: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name.trim() || f.phone.replace(/\D/g, "").length < 7) {
      toast.error("Please add your name and a valid phone number.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...f, items: items.map(({ category, name, qty, note }) => ({ category, name, qty, note })) };
      const { data } = await api.post("/orders", payload);
      clear();
      onDone(data);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="checkout-form">
      <p className="f-mono pt-2 text-xs uppercase tracking-[0.2em] text-cacao-2">Your details</p>
      <input className={field} placeholder="Full name *" value={f.customer_name} onChange={set("customer_name")} data-testid="checkout-name-input" />
      <input className={field} placeholder="Phone number *" type="tel" value={f.phone} onChange={set("phone")} data-testid="checkout-phone-input" />
      <input className={field} placeholder="Email (optional)" type="email" value={f.email} onChange={set("email")} data-testid="checkout-email-input" />
      <div className="grid grid-cols-2 gap-2">
        {["pickup", "delivery"].map((m) => (
          <button
            type="button"
            key={m}
            onClick={() => setF({ ...f, fulfillment: m })}
            data-testid={`checkout-fulfillment-${m}`}
            className={`rounded-xl border py-3 text-sm font-medium capitalize transition-colors ${f.fulfillment === m ? "border-[#2D1E18] bg-[#2D1E18] text-white" : "border-[#F0DDD3] bg-white text-cacao"}`}
          >
            {m}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-xs text-cacao-2">Date needed</span>
          <input type="date" min={today()} className={field} value={f.date_needed} onChange={set("date_needed")} data-testid="checkout-date-input" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-cacao-2">Occasion</span>
          <input className={field} placeholder="Birthday…" value={f.occasion} onChange={set("occasion")} data-testid="checkout-occasion-input" />
        </label>
      </div>
      <textarea rows={3} className={field} placeholder="Anything else? Colors, theme, allergies, delivery address…" value={f.notes} onChange={set("notes")} data-testid="checkout-notes-input" />
      <button
        type="submit"
        disabled={busy}
        data-testid="checkout-submit-button"
        className="w-full rounded-full bg-[#E11D48] py-4 text-sm font-semibold text-white shadow-[0_14px_30px_-12px_rgba(225,29,72,0.7)] transition-[transform,opacity] hover:-translate-y-0.5 disabled:opacity-60"
      >
        {busy ? "Placing order…" : "Place order & get quote"}
      </button>
      <p className="text-center text-xs text-cacao-2">Next, you'll send it to us by text or email.</p>
    </form>
  );
};

export const OrderDrawer = () => {
  const { items, open, setOpen, count } = useCart();
  const [done, setDone] = useState(null);
  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) setDone(null);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto border-l-0 bg-cream p-0 sm:max-w-lg" data-lenis-prevent data-testid="order-drawer">
        <div className="p-6 sm:p-8">
          {done ? (
            <OrderSuccess order={done} />
          ) : (
            <>
              <SheetHeader className="mb-6 text-left">
                <SheetTitle className="f-display text-4xl font-semibold text-cacao">My Sweet Box</SheetTitle>
                <SheetDescription className="text-cacao-2">{count} {count === 1 ? "treat" : "treats"} • price quoted after you order</SheetDescription>
              </SheetHeader>
              {items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#E9CFC3] p-10 text-center" data-testid="box-empty-state">
                  <p className="f-display text-2xl italic text-cacao">Your box is empty</p>
                  <p className="mt-2 text-sm text-cacao-2">Add a few flavors from the menu to get started.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <ul className="space-y-3" data-testid="box-items-list">
                    {items.map((it) => <BoxItem key={it.key} it={it} />)}
                  </ul>
                  <CheckoutForm onDone={setDone} />
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
